import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  formattedCurrency,
  GetColorStatus,
  capitalizeFirstLetterOfWords,
  MaterialSymbol,
  Modal,
  Workflow,
} from 'vopay';
import {
  Title,
  Button,
  UtilButton,
  DetailsRows,
  TransactionStatus,
  NoFillButton,
  Skeleton,
  TransactionTypeLabel,
} from 'UI';
import { useTranslation } from 'react-i18next';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import usePostUpdateWorkflow from '@/hooks/useApi/usePostUpdateWorkflow';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import DetailsCard from '@/components/UI/DetailsCard';
import TierBadge from './UpdateWorkFlow/ApprovalTiers/TierBadge';
import WorkflowChangeHistory from './WorkflowChangeHistory';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import { useClientData } from '@/providers/ClientData.provider';
import DeactivateWithSubAccountsDialog from './UpdateWorkFlow/DeactivateWithSubAccountsDialog';
import DeactivateWithoutSubAccountsDialog from './UpdateWorkFlow/DeactivateWithoutSubAccountsDialog';
import ActivateWithSubAccountsDialog from './UpdateWorkFlow/ActivateWithSubAccountsDialog';
import ActivateWithoutSubAccountsDialog from './UpdateWorkFlow/ActivateWithoutSubAccountsDialog';
import styles from './WorkflowDetailsContainer.module.scss';

type WorkflowDetailsContainerProps = {
  bearer?: string;
  workflowId?: string;
  accountId?: string;
  onBack?: () => void;
  onEdit?: (id: string) => void;
};

type PendingToggle =
  | null
  | { type: 'deactivateWithSub'; orderedTiers: Workflow[]; applyToAllSubaccounts: boolean }
  | { type: 'deactivateWithoutSub'; orderedTiers: Workflow[]; applyToAllSubaccounts: boolean }
  | { type: 'activateWithSub'; orderedTiers: Workflow[]; applyToAllSubaccounts: boolean }
  | { type: 'activateWithoutSub'; orderedTiers: Workflow[]; applyToAllSubaccounts: boolean };

const WorkflowDetailsContainer: FunctionComponent<WorkflowDetailsContainerProps> = ({
  bearer,
  workflowId: workflowIdProp,
  accountId,
  onBack,
  onEdit,
}) => {
  const history = useHistory();
  const { id: routeId } = useParams<{ id: string }>();
  const id = workflowIdProp ?? routeId;
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Workflows.Details' });
  const {
    response: { subAccountPermissions },
  } = useClientData();
  const { workflows, fetchWorkflows, fetchingWorkflows, workflowLogs, fetchWorkflowLogs, fetchingWorkflowLogs } =
    useFetchWorkflows({ bearer });
  const alertDialog = useAlertDialog();
  const [, setLoading] = useDataFetchIndicator();
  const [, , updateWorkflowAsync] = usePostUpdateWorkflow({});
  const [, , , fetchSubAccountsAsync] = useFetchSubAccounts({});
  const [subAccountCount, setSubAccountCount] = useState(0);
  const [pendingToggle, setPendingToggle] = useState<PendingToggle>(null);

  useEffect(() => {
    fetchWorkflows().catch(() => alertDialog.error(tGlobal('error'), tGlobal('errorOccurred')));
    fetchWorkflowLogs().catch(() => alertDialog.error(tGlobal('error'), tGlobal('errorOccurred')));
  }, []);

  useEffect(() => {
    if (!subAccountPermissions) return;
    fetchSubAccountsAsync({ ActiveOnly: true, ...(accountId ? { SubaccountID: accountId } : {}) }).then((res) => {
      if (res?.Success) setSubAccountCount(res.Subaccounts?.length ?? 0);
    });
  }, [subAccountPermissions, accountId]);

  const group = workflows.find((g) => g.Workflows[0]?.WorkflowID?.toString() === id);
  const isActive = group?.Workflows.some((wf) => wf.IsActive);
  const sortedTiers = [...(group?.Workflows || [])].sort((a, b) => parseFloat(a.MinAmount) - parseFloat(b.MinAmount));
  const filteredWorkflowLogs = workflowLogs.filter((log) => log.TransactionType === group?.TransactionType);

  const executeToggle = async (orderedTiers: Workflow[], applyToAllSubaccounts: boolean) => {
    try {
      for (const wf of orderedTiers) {
        const res = await updateWorkflowAsync(
          { ...wf, IsActive: !isActive, ApplyToAllSubaccounts: applyToAllSubaccounts },
          bearer,
        );
        if (!res.Success) {
          alertDialog.error(tGlobal('error'), res.ErrorMessage || tGlobal('errorOccurred'));
          fetchWorkflows();
          return;
        }
      }

      if (applyToAllSubaccounts) {
        setLoading(true, '', 'fullScreen');
        const subAccountsRes = await fetchSubAccountsAsync({
          ActiveOnly: true,
          ShowToken: true,
          ...(accountId ? { SubaccountID: accountId } : {}),
        });
        if (subAccountsRes?.Success && subAccountsRes.Subaccounts) {
          await Promise.allSettled(
            subAccountsRes.Subaccounts.map(async (subAccount) => {
              const bearer = subAccount.Token;
              if (!bearer) return;
              const subWorkflowsRes = await fetchWorkflows(bearer);
              const subGroup = subWorkflowsRes?.Workflows.find((g) => g.TransactionType === group?.TransactionType);
              if (!subGroup) return;
              const subOrdered = isActive
                ? [...subGroup.Workflows].sort((a, b) => parseFloat(b.MinAmount) - parseFloat(a.MinAmount))
                : [...subGroup.Workflows].sort((a, b) => parseFloat(a.MinAmount) - parseFloat(b.MinAmount));
              for (const wf of subOrdered) {
                await updateWorkflowAsync(
                  { ...wf, IsActive: !isActive, ApplyToAllSubaccounts: applyToAllSubaccounts },
                  bearer,
                );
              }
            }),
          );
        }
      }

      fetchWorkflows();
    } catch {
      alertDialog.error(tGlobal('error'), tGlobal('errorOccurred'));
      fetchWorkflows();
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async () => {
    const applyToAllSubaccounts = group?.ApplyToAllSubaccounts ?? false;
    // Deactivate: highest tier first → lowest; Activate: lowest tier first → highest
    const orderedTiers = isActive ? [...sortedTiers].reverse() : [...sortedTiers];

    if (subAccountPermissions && applyToAllSubaccounts) {
      setPendingToggle({
        type: isActive ? 'deactivateWithSub' : 'activateWithSub',
        orderedTiers,
        applyToAllSubaccounts,
      });
      return;
    } else if (subAccountPermissions && !applyToAllSubaccounts) {
      setPendingToggle({
        type: isActive ? 'deactivateWithoutSub' : 'activateWithoutSub',
        orderedTiers,
        applyToAllSubaccounts,
      });
      return;
    } else {
      const result = await alertDialog.caution(
        isActive
          ? t('Dialogue.deactivateTitle', { defaultValue: 'Deactivate Workflow' })
          : t('Dialogue.activateTitle', { defaultValue: 'Activate Workflow' }),
        isActive
          ? t('Dialogue.deactivateContent', { defaultValue: 'Are you sure you want to deactivate this workflow?' })
          : t('Dialogue.activateContent', { defaultValue: 'Are you sure you want to activate this workflow?' }),
        {
          confirmLabel: isActive
            ? t('Dialogue.deactivateConfirm', { defaultValue: 'Deactivate' })
            : t('Dialogue.activateConfirm', { defaultValue: 'Activate' }),
          abortLabel: tGlobal('Buttons.cancel'),
        },
      );
      if (result !== 'confirm') return;
    }

    await executeToggle(orderedTiers, applyToAllSubaccounts);
  };

  const handleToggleConfirm = () => {
    if (!pendingToggle) return;
    const { orderedTiers, applyToAllSubaccounts } = pendingToggle;
    setPendingToggle(null);
    executeToggle(orderedTiers, applyToAllSubaccounts);
  };

  const formatAmount = (amount: string | number | null) => formattedCurrency((amount ?? '0').toString());

  return (
    <div className={styles.page}>
      {onBack ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <Button
            colorScheme="primary"
            onClick={() =>
              onEdit ? onEdit(id) : history.push(`/transaction-approval/configuration/workflows/edit/${id}`)
            }
          >
            {t('editWorkflow', { defaultValue: 'Edit Workflow' })}
          </Button>
        </div>
      ) : (
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <UtilButton
              iconLeft="chevron_left"
              size="large"
              onClick={() => {
                if (history.length > 1) {
                  history.goBack();
                } else {
                  history.replace('/transaction-approval/configuration/workflows');
                }
              }}
            >
              {tGlobal('Buttons.back')}
            </UtilButton>
            <Title level="1">{t('title', { defaultValue: 'Workflow Details' })}</Title>
          </div>
          <Button
            colorScheme="primary"
            onClick={() =>
              onEdit ? onEdit(id) : history.push(`/transaction-approval/configuration/workflows/edit/${id}`)
            }
          >
            {t('editWorkflow', { defaultValue: 'Edit Workflow' })}
          </Button>
        </div>
      )}

      <DetailsCard className={styles.container} style={{ padding: '24px' }}>
        <Title level="4">{t('Configuration.title', { defaultValue: 'Configuration' })}</Title>
        {fetchingWorkflows ? (
          <div className={styles.skeletonList}>
            <Skeleton height="20px" />
            <Skeleton height="20px" />
            <Skeleton height="20px" />
          </div>
        ) : (
          <DetailsRows
            details={[
              {
                label: t('Configuration.paymentRail', { defaultValue: 'Payment Rail' }),
                value: <TransactionTypeLabel transactionType={group?.TransactionType || '-'} />,
              },
              {
                label: t('Configuration.status', { defaultValue: 'Status' }),
                value: (
                  <div className={styles.statusValue}>
                    <TransactionStatus
                      status={GetColorStatus(group?.Workflows[0]?.IsActive ? 'active' : 'inactive')}
                      label={capitalizeFirstLetterOfWords(
                        group?.Workflows[0]?.IsActive
                          ? t('Configuration.active', { defaultValue: 'Active' })
                          : t('Configuration.inactive', { defaultValue: 'Inactive' }),
                      )}
                    />
                  </div>
                ),
              },
              ...(subAccountPermissions
                ? [
                    {
                      label: t('Configuration.appliesToSubAccounts', { defaultValue: 'Applies to Sub-accounts' }),
                      value: group?.ApplyToAllSubaccounts
                        ? tGlobal('yes', { defaultValue: 'Yes' })
                        : tGlobal('no', { defaultValue: 'No' }),
                    },
                  ]
                : []),
              {
                label: t('Configuration.createdOn', { defaultValue: 'Created On' }),
                value: group?.Workflows[0]?.DateAdded || '-',
              },
            ]}
          />
        )}
        <DetailsCard style={{ padding: '16px' }}>
          <div className={styles.approvalHeading}>
            <Title level="5">{t('ApprovalTiers.title', { defaultValue: 'Approval Tiers' })}</Title>
            <div className={styles.description}>
              {t('ApprovalTiers.description', {
                defaultValue: 'Approval rules applied to transactions on this payment rail.',
              })}
            </div>
          </div>
          <div className={`${styles.tableGrid} ${styles.tableHeader}`}>
            <span className={styles.tableHeaderCell}>{t('ApprovalTiers.tier', { defaultValue: 'Tier' })}</span>
            <span className={styles.tableHeaderCell}>
              {t('ApprovalTiers.amountRange', { defaultValue: 'Amount Range' })}
            </span>
            <span className={styles.tableHeaderCell}>
              {t('ApprovalTiers.approversRequired', { defaultValue: 'Approvers Required' })}
            </span>
          </div>
          {fetchingWorkflows ? (
            <div className={styles.skeletonTiers}>
              <Skeleton height="40px" />
              <Skeleton height="40px" />
            </div>
          ) : (
            sortedTiers.map((wf, index) => {
              const startDisplay =
                index === 0 ? (
                  `CAD ${formatAmount(wf.MinAmount)}`
                ) : (
                  <>
                    <span style={{ position: 'relative', top: '-1px' }}>&gt;</span>
                    {` CAD ${formatAmount(wf.MinAmount)}`}
                  </>
                );
              const endDisplay =
                wf.MaxAmount !== null
                  ? `CAD ${formatAmount(wf.MaxAmount)}`
                  : t('ApprovalTiers.noLimit', { defaultValue: 'No limit' });
              return (
                <div key={wf.WorkflowID} className={`${styles.tableGrid} ${styles.tableRow}`}>
                  <TierBadge index={index} />
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {startDisplay}
                    <MaterialSymbol icon="arrow_forward" />
                    {endDisplay}
                  </span>
                  <span>{Number(wf.NumberOfApprovalsRequired).toLocaleString()}</span>
                </div>
              );
            })
          )}
        </DetailsCard>
      </DetailsCard>
      {filteredWorkflowLogs.length > 0 && (
        <WorkflowChangeHistory logs={filteredWorkflowLogs} loading={fetchingWorkflowLogs} />
      )}
      <NoFillButton
        className={styles.toggleButton}
        colorScheme={isActive ? 'destructive' : 'neutral'}
        onClick={handleToggleActive}
      >
        {isActive ? t('deactivate', { defaultValue: 'Deactivate' }) : t('activate', { defaultValue: 'Activate' })}
      </NoFillButton>

      {pendingToggle !== null && (
        <Modal isVisible={true} enableBgClick={false}>
          {pendingToggle.type === 'deactivateWithSub' ? (
            <DeactivateWithSubAccountsDialog
              subAccountCount={subAccountCount}
              onConfirm={handleToggleConfirm}
              onCancel={() => setPendingToggle(null)}
            />
          ) : pendingToggle.type === 'deactivateWithoutSub' ? (
            <DeactivateWithoutSubAccountsDialog
              onConfirm={handleToggleConfirm}
              onCancel={() => setPendingToggle(null)}
            />
          ) : pendingToggle.type === 'activateWithSub' ? (
            <ActivateWithSubAccountsDialog
              subAccountCount={subAccountCount}
              onConfirm={handleToggleConfirm}
              onCancel={() => setPendingToggle(null)}
            />
          ) : (
            <ActivateWithoutSubAccountsDialog onConfirm={handleToggleConfirm} onCancel={() => setPendingToggle(null)} />
          )}
        </Modal>
      )}
    </div>
  );
};

export default WorkflowDetailsContainer;
